import { useState, type FormEvent } from 'react';
import { ApiError } from '../api/client';
import {
  useCreateLeadStatus,
  useDeleteLeadStatus,
  useLeadStatuses,
  useRestoreLeadStatus,
} from '../api/hooks';
import type { LeadStatusDef, StatusCategory } from '../api/types';
import { EmptyState, ErrorBanner, Modal, StatusChip, TableSkeleton } from '../components/ui';

const CATEGORIES: Array<{ value: StatusCategory; label: string; hint: string }> = [
  { value: 'open', label: 'Open', hint: 'Not yet worked' },
  { value: 'working', label: 'Working', hint: 'Being followed up' },
  { value: 'won', label: 'Won', hint: 'Counts as a conversion' },
  { value: 'lost', label: 'Lost', hint: 'Closed, did not convert' },
  { value: 'invalid', label: 'Invalid', hint: 'Junk or duplicate' },
];

const SWATCHES = ['#6B7280', '#3B82F6', '#0EA5E9', '#14B8A6', '#22C55E', '#EAB308', '#F97316', '#EF4444', '#EC4899', '#8B5CF6'];

/**
 * Settings → Lead statuses. Admins add and delete the statuses counsellors
 * pick from. Deleting moves any leads still in the status to another one, then
 * hides it everywhere; it stays restorable because past timelines name it.
 */
export function LeadStatusesPage() {
  const { data: statuses = [], isPending, error } = useLeadStatuses(true);
  const restore = useRestoreLeadStatus();
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState<LeadStatusDef | null>(null);
  const [notice, setNotice] = useState('');
  const [actionError, setActionError] = useState<unknown>(null);

  const active = statuses.filter((s) => s.is_active);
  const deleted = statuses.filter((s) => !s.is_active);

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Lead statuses</h1>
          <p className="sub">
            {active.length} active {active.length === 1 ? 'status' : 'statuses'} · these are the
            only statuses team specialists can choose when updating a lead
          </p>
        </div>
        <div className="page-head-actions">
          <button className="btn btn-primary" onClick={() => { setNotice(''); setAdding(true); }}>
            + Add status
          </button>
        </div>
      </div>

      {notice && <div className="banner banner-success" role="status">{notice}</div>}
      <ErrorBanner error={error ?? actionError} />

      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Status</th>
              <th>Category</th>
              <th className="num">Leads</th>
              <th className="num">Sub statuses</th>
              <th />
            </tr>
          </thead>
          {isPending ? (
            <TableSkeleton rows={8} cols={5} />
          ) : (
            <tbody>
              {active.map((s) => (
                <tr key={s.id}>
                  <td>
                    <StatusChip status={s} />
                    {s.is_default && <span className="tag" style={{ marginLeft: 8 }}>New leads start here</span>}
                  </td>
                  <td style={{ textTransform: 'capitalize' }}>{s.category}</td>
                  <td className="num">{s.lead_count.toLocaleString()}</td>
                  <td className="num">{s.sub_status_count || '—'}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      className="btn btn-sm btn-danger"
                      disabled={s.is_default || active.length <= 1}
                      title={
                        s.is_default
                          ? 'New leads start in this status, so it cannot be deleted'
                          : active.length <= 1 ? 'At least one status must remain' : undefined
                      }
                      onClick={() => { setNotice(''); setActionError(null); setDeleting(s); }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </div>

      {deleted.length > 0 && (
        <>
          <h2 className="section-title">Deleted statuses</h2>
          <p className="sub" style={{ marginTop: -6 }}>
            Hidden from every picker. Old lead timelines still show them; restore one to make it
            selectable again.
          </p>
          <div className="table-wrap">
            <table className="data">
              <tbody>
                {deleted.map((s) => (
                  <tr key={s.id}>
                    <td style={{ opacity: 0.65 }}><StatusChip status={s} /></td>
                    <td style={{ textTransform: 'capitalize' }}>{s.category}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        className="btn btn-sm"
                        disabled={restore.isPending}
                        onClick={async () => {
                          setActionError(null);
                          try {
                            await restore.mutateAsync(s.key);
                            setNotice(`"${s.name}" restored.`);
                          } catch (err) {
                            setActionError(err);
                          }
                        }}
                      >
                        Restore
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!isPending && statuses.length === 0 && <EmptyState title="No statuses yet" />}

      {adding && (
        <AddStatusModal
          onClose={() => setAdding(false)}
          onCreated={(name) => { setAdding(false); setNotice(`"${name}" added.`); }}
        />
      )}
      {deleting && (
        <DeleteStatusModal
          status={deleting}
          others={active.filter((s) => s.key !== deleting.key)}
          onClose={() => setDeleting(null)}
          onDeleted={(moved, target) => {
            setDeleting(null);
            setNotice(
              moved > 0
                ? `"${deleting.name}" deleted. ${moved.toLocaleString()} ${moved === 1 ? 'lead was' : 'leads were'} moved to "${target}".`
                : `"${deleting.name}" deleted.`,
            );
          }}
        />
      )}
    </div>
  );
}

function AddStatusModal({ onClose, onCreated }: { onClose: () => void; onCreated: (name: string) => void }) {
  const create = useCreateLeadStatus();
  const [name, setName] = useState('');
  const [category, setCategory] = useState<StatusCategory>('working');
  const [color, setColor] = useState(SWATCHES[1]!);
  const [error, setError] = useState<unknown>(null);
  const fieldErrors = error instanceof ApiError ? error.fieldErrors : {};

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const created = await create.mutateAsync({ name: name.trim(), category, color });
      onCreated(created.name);
    } catch (err) {
      setError(err);
    }
  }

  return (
    <Modal
      title="Add status"
      onClose={onClose}
      width={480}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" form="status-form" disabled={create.isPending || !name.trim()}>
            {create.isPending ? <span className="spinner" /> : 'Add status'}
          </button>
        </>
      }
    >
      <ErrorBanner error={error} />
      <form id="status-form" onSubmit={submit}>
        <div className={`field${fieldErrors.name ? ' invalid' : ''}`}>
          <label htmlFor="st-name">Name<span className="req">*</span></label>
          <input id="st-name" autoFocus maxLength={60} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Fee Discussion" />
        </div>
        <div className="field">
          <label htmlFor="st-category">Category</label>
          <select id="st-category" value={category} onChange={(e) => setCategory(e.target.value as StatusCategory)}>
            {CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label} — {c.hint}</option>
            ))}
          </select>
          <div className="field-hint">Reports use the category, so “Won” statuses count as conversions.</div>
        </div>
        <div className="field">
          <label>Colour</label>
          <div className="swatches" role="radiogroup" aria-label="Colour">
            {SWATCHES.map((c) => (
              <button
                key={c}
                type="button"
                role="radio"
                aria-checked={color === c}
                aria-label={c}
                className="swatch"
                data-on={color === c}
                style={{ background: c }}
                onClick={() => setColor(c)}
              />
            ))}
          </div>
        </div>
        {name.trim() && (
          <div className="field">
            <label>Preview</label>
            <StatusChip status={{ id: '', key: '', name: name.trim(), color, category }} />
          </div>
        )}
      </form>
    </Modal>
  );
}

function DeleteStatusModal({
  status,
  others,
  onClose,
  onDeleted,
}: {
  status: LeadStatusDef;
  others: LeadStatusDef[];
  onClose: () => void;
  onDeleted: (moved: number, targetName: string) => void;
}) {
  const remove = useDeleteLeadStatus();
  const [moveTo, setMoveTo] = useState('');
  const [error, setError] = useState<unknown>(null);
  const hasLeads = status.lead_count > 0;

  async function confirm() {
    setError(null);
    try {
      const res = await remove.mutateAsync({ key: status.key, move_to: hasLeads ? moveTo : undefined });
      onDeleted(res.moved_leads, others.find((o) => o.key === moveTo)?.name ?? '');
    } catch (err) {
      setError(err);
    }
  }

  return (
    <Modal
      title={`Delete "${status.name}"?`}
      onClose={onClose}
      width={500}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>Cancel</button>
          <button className="btn btn-danger" disabled={remove.isPending || (hasLeads && !moveTo)} onClick={confirm}>
            {remove.isPending ? <span className="spinner" /> : hasLeads ? 'Move leads and delete' : 'Delete status'}
          </button>
        </>
      }
    >
      <ErrorBanner error={error} />
      <p style={{ marginTop: 0 }}>
        Team Specialists will no longer be able to choose <b>{status.name}</b>. Past timelines keep
        showing it, and you can restore it later.
      </p>
      {status.sub_status_count > 0 && (
        <p>Its {status.sub_status_count} sub {status.sub_status_count === 1 ? 'status is' : 'statuses are'} hidden with it.</p>
      )}
      {status.used_by && (
        <div className="banner banner-info"><div>This status feeds {status.used_by}, which will read zero after it is deleted.</div></div>
      )}
      {hasLeads ? (
        <div className="field">
          <label htmlFor="move-to">
            Move {status.lead_count.toLocaleString()} {status.lead_count === 1 ? 'lead' : 'leads'} to<span className="req">*</span>
          </label>
          <select id="move-to" value={moveTo} onChange={(e) => setMoveTo(e.target.value)}>
            <option value="">Choose a status…</option>
            {others.map((o) => (
              <option key={o.key} value={o.key}>{o.name}</option>
            ))}
          </select>
          <div className="field-hint">Each lead's timeline records the move. Their sub statuses are cleared.</div>
        </div>
      ) : (
        <p style={{ color: 'var(--muted)' }}>No leads are in this status.</p>
      )}
    </Modal>
  );
}
